import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useEffect} from 'react';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import styles from './vector-fill-thumbnail-option.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {isObject} from 'lodash';
import {convertRgbaArrayColorsToRGBString, getRgbaArrayFromFabricColor, convertFabricColorToFillObject, getNewFabricColorForFillType} from '@Utils/color.util';

interface VectorFillThumbnailOptionProps {
  panelId: string;
}

export function VectorFillThumbnailOption({...props}: VectorFillThumbnailOptionProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const fill = useGetKeyFromActiveItem('fill');

  if (fill === undefined) {
    return null;
  }

  const fillObject = useMemo(() => {
    return convertFabricColorToFillObject(fill);
  }, [fill]);
  const fillType = fillObject.fillType;

  useEffect((): void => {
    if (activeItem?.isVector() && fillType !== undefined) {
      updateHeight(fillType);
    }
  }, [fillType]);

  if (!activeItem || fillType === undefined) {
    return null;
  }

  if (!activeItem.isVector() && !activeItem.isRectangle()) {
    return null;
  }

  const getBackgroundColorFromType = (type: FillTypes): string => {
    switch (type) {
      case FillTypes.LINEAR_GRADIENT:
        return styles.linear;
      case FillTypes.RADIAL_GRADIENT:
        return styles.radial;
      case FillTypes.SOLID:
      default:
        return styles.solid;
    }
  };

  const getThumbnail = (type: FillTypes): ReactElement => {
    return <div className={`${styles.fillThumbnail} radius-round ${getBackgroundColorFromType(type)}`} />;
  };

  const updateHeight = (type: FillTypes): void => {
    let height = '180px';
    switch (type) {
      case FillTypes.LINEAR_GRADIENT:
      case FillTypes.RADIAL_GRADIENT:
        height = '352px';
        break;
      case FillTypes.SOLID:
        height = '304px';
        break;
      case FillTypes.NONE:
      default:
        break;
    }

    updateOptionsPanelHeight(props.panelId, height);
  };

  const updateFillType = (type: FillTypes): void => {
    if (fillType === type) {
      return;
    }

    void activeItem.updateFromObject({
      fill: getNewFabricColorForFillType(type, convertRgbaArrayColorsToRGBString(getRgbaArrayFromFabricColor(fill))),
    });
    updateHeight(type);
  };

  return (
    <ul key={fillType} className="spacing-m-0 flex-h-row">
      <ContextualMenuThumbnailItem
        thumbnail={<Icon icon="icon-ban" size={IconSize.SIZE_ICON_32} />}
        isSelected={fill === null}
        selectionState={ThumbnailItemSelectionState.BORDER}
        text={window.i18next.t('pmwjs_none')}
        onClick={(): void => {
          updateFillType(FillTypes.NONE);
        }}
        isMultiLineText
      />
      <ContextualMenuThumbnailItem
        thumbnail={getThumbnail(FillTypes.SOLID)}
        isSelected={typeof fill === 'string'}
        selectionState={ThumbnailItemSelectionState.BORDER}
        text={window.i18next.t('pmwjs_background_solid')}
        onClick={(): void => {
          updateFillType(FillTypes.SOLID);
        }}
        isMultiLineText
      />
      <ContextualMenuThumbnailItem
        thumbnail={getThumbnail(FillTypes.LINEAR_GRADIENT)}
        isSelected={isObject(fill) && fill.type === 'linear'}
        selectionState={ThumbnailItemSelectionState.BORDER}
        text={window.i18next.t('pmwjs_linear_gradient_background')}
        textClassName={`spacing-p-l-2 spacing-p-r-2 ${styles.wrappedText}`}
        onClick={(): void => {
          updateFillType(FillTypes.LINEAR_GRADIENT);
        }}
        isMultiLineText
      />
      <ContextualMenuThumbnailItem
        thumbnail={getThumbnail(FillTypes.RADIAL_GRADIENT)}
        isSelected={isObject(fill) && fill.type === 'radial'}
        selectionState={ThumbnailItemSelectionState.BORDER}
        text={window.i18next.t('pmwjs_radial_gradient_background')}
        textClassName={`spacing-p-l-2 spacing-p-r-2 ${styles.wrappedText}`}
        onClick={(): void => {
          updateFillType(FillTypes.RADIAL_GRADIENT);
        }}
        isMultiLineText
      />
    </ul>
  );
}
